import {createPortal} from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { api, errMsg } from '@/lib/api';
import { tr, uiLocale } from '@/i18n/copy';
import { movableAppointment, appointmentDuration, targetStart, targetDay, movePreference, movePayload, movePreview } from '@/lib/smartAppointmentMove';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

// Touch Events are used deliberately: a non-passive move listener prevents
// scrolling only AFTER the stationary hold. Ordinary swipes remain native.
export default function useSmartAppointmentMove({ timeZone, active, preferenceKey, onMoved }) {
  const [available, setAvailable] = useState(false);
  const [preference, setPreference] = useState(() => ({key: preferenceKey, enabled: movePreference(preferenceKey)}));
  const enabled = preference.key === preferenceKey ? preference.enabled : movePreference(preferenceKey);
  const toggle = () => {
    const next = !enabled;
    setPreference({key: preferenceKey, enabled: next});
    try { if (preferenceKey) window.localStorage.setItem(preferenceKey, String(next)); } catch {}
  };
  const [visual, setVisual] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const gesture = useRef(null), suppress = useRef(0), timer = useRef(null), frame = useRef(null);
  const flight = useRef(false), current = useRef({});
  current.current = { timeZone, onMoved, draft };
  const usable = available && enabled && active && !draft && !busy && !checking;
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    api.get('/scheduling/smart-move').then(r => { if (alive.current) setAvailable(r?.enabled === true); }).catch(() => {});
    return () => { alive.current = false; };
  }, []);
  const label = value => new Date(value).toLocaleString(uiLocale(), { timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const reset = () => {
    window.clearTimeout(timer.current); window.cancelAnimationFrame?.(frame.current);
    gesture.current = null; if (alive.current) setVisual(null);
  };
  const cancel = () => { if (gesture.current?.armed) suppress.current = Date.now() + 700; reset(); };
  const point = (g, x, y) => {
    g.x = x; g.y = y;
    const column = document.elementFromPoint(x, y)?.closest('[data-smart-timegrid], [data-smart-daygrid]');
    const rect = column?.getBoundingClientRect();
    const dayGrid = column?.hasAttribute('data-smart-daygrid');
    g.gridStart = Number(column?.dataset.smartStartMinute ?? 600);
    g.gridEnd = Number(column?.dataset.smartEndMinute ?? 1140);
    const start = !rect ? null : dayGrid ? targetDay(column.dataset.calendarDay, g.appointment, current.current.timeZone)
      : targetStart(column.dataset.calendarDay, y - rect.top - g.grabOffset, rect.height, appointmentDuration(g.appointment), current.current.timeZone, g.gridStart, g.gridEnd);
    g.start = start && new Date(start) > new Date() ? start : null;
    const preview = movePreview(g,rect,window.innerWidth,window.innerHeight);
    if (dayGrid && g.start) preview.target = {left:rect.left+2,top:rect.top+2,width:rect.width-4,height:rect.height-4};
    if (alive.current) setVisual({ id: g.appointment.id, title:g.appointment.title, armed: true, x, y, start: g.start, ...preview });
  };
  const scroll = () => {
    const g = gesture.current;
    if (!g?.armed) return;
    const scroller = document.querySelector('[data-calendar-view]');
    const rect = scroller?.getBoundingClientRect();
    if (rect) {
      const left = Math.max(0, rect.left), right = Math.min(window.innerWidth, rect.right);
      if (g.x < left + 36) scroller.scrollLeft -= 10;
      else if (g.x > right - 36) scroller.scrollLeft += 10;
    }
    if (g.y < 60) window.scrollBy(0, -8);
    else if (g.y > window.innerHeight - 60) window.scrollBy(0, 8);
    point(g, g.x, g.y);
    frame.current = window.requestAnimationFrame(scroll);
  };
  const prepare = async (appointment, start) => {
    if (!start || new Date(start).getTime() === new Date(appointment.start).getTime()) return;
    flight.current = true; setChecking(true); setError('');
    try {
      const fresh = await api.get(`/appointments/${appointment.id}`);
      if (!movableAppointment(fresh) || fresh.start !== appointment.start || fresh.updated_at !== appointment.updated_at) throw new Error(tr('L’appuntamento è cambiato. Aggiorna il calendario e riprova.'));
      const payload = movePayload(fresh, start);
      if (alive.current) { setDraft({ appointment: fresh, payload }); setUncertain(false); }
    } catch (e) { if (alive.current) toast.error(errMsg(e)); }
    finally { flight.current = false; if (alive.current) setChecking(false); }
  };
  const startHold = (event, appointment, kind, id, x, y) => {
    if (!usable || flight.current || !movableAppointment(appointment) || gesture.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const g = { appointment, kind, id, x, y, originX: x, originY: y, armed: false, grabOffset: y - rect.top, width:rect.width, height:rect.height };
    gesture.current = g;
    setVisual({ id: appointment.id, armed: false, x, y });
    timer.current = window.setTimeout(() => {
      if (gesture.current !== g) return;
      g.armed = true; point(g, g.x, g.y);
      frame.current = window.requestAnimationFrame(scroll);
    }, 1000);
  };
  const handlers = useRef({});
  handlers.current = { point, reset, cancel, prepare };
  useEffect(() => {
    if (!usable) { handlers.current.cancel(); return undefined; }
    const move = (event, kind) => {
      const g = gesture.current;
      if (!g || g.kind !== kind) return;
      const p = kind === 'touch' ? [...event.touches].find(t => t.identifier === g.id) : event;
      if (!p || (kind === 'touch' && event.touches.length !== 1)) { handlers.current.cancel(); return; }
      if (kind !== 'touch' && event.pointerId !== g.id) return;
      if (!g.armed) {
        if (Math.hypot(p.clientX - g.originX, p.clientY - g.originY) > 8) handlers.current.cancel();
        return;
      }
      if (event.cancelable) event.preventDefault();
      handlers.current.point(g, p.clientX, p.clientY);
    };
    const end = (event, kind) => {
      const g = gesture.current;
      if (!g || g.kind !== kind || (kind !== 'touch' && event.pointerId !== g.id)) return;
      if (kind === 'touch' && ![...event.changedTouches].some(t => t.identifier === g.id)) return;
      if (g.armed) {
        if (event.cancelable) event.preventDefault();
        suppress.current = Date.now() + 700;
        handlers.current.reset(); handlers.current.prepare(g.appointment, g.start);
      } else handlers.current.reset();
    };
    const pm = e => move(e, 'pointer'), pu = e => end(e, 'pointer');
    const tm = e => move(e, 'touch'), tu = e => end(e, 'touch');
    const abort = () => handlers.current.cancel();
    const key = e => { if (e.key === 'Escape') abort(); };
    const hidden = () => { if (document.visibilityState === 'hidden') abort(); };
    const nativeScroll = () => { if (gesture.current && !gesture.current.armed) abort(); };
    document.addEventListener('pointermove', pm, { passive: false });
    document.addEventListener('pointerup', pu);
    document.addEventListener('pointercancel', abort);
    document.addEventListener('touchmove', tm, { passive: false });
    document.addEventListener('touchend', tu, { passive: false });
    document.addEventListener('touchcancel', abort);
    document.addEventListener('keydown', key);
    document.addEventListener('visibilitychange', hidden);
    document.addEventListener('scroll', nativeScroll, true);
    window.addEventListener('blur', abort);
    return () => {
      abort();
      document.removeEventListener('pointermove', pm); document.removeEventListener('pointerup', pu);
      document.removeEventListener('pointercancel', abort); document.removeEventListener('touchmove', tm);
      document.removeEventListener('touchend', tu); document.removeEventListener('touchcancel', abort);
      document.removeEventListener('keydown', key); document.removeEventListener('visibilitychange', hidden);
      document.removeEventListener('scroll', nativeScroll, true); window.removeEventListener('blur', abort);
    };
  }, [usable]);
  const done = appointment => {
    if (!alive.current) return;
    setDraft(null); setUncertain(false); setError('');
    toast.success(tr('Appuntamento riprogrammato'));
    current.current.onMoved(appointment);
  };
  const reconcile = async (pending, message) => {
    setUncertain(true);
    try {
      const original = await api.get(`/appointments/${pending.appointment.id}`);
      if (original.rescheduled_to) {
        const replacement = await api.get(`/appointments/${original.rescheduled_to}`);
        if (replacement.rescheduled_from === original.id && new Date(replacement.start).getTime() === new Date(pending.payload.start).getTime()
          && appointmentDuration(replacement) === pending.payload.duration_minutes) { done(replacement); return; }
        setError(tr('L’appuntamento è stato riprogrammato da un’altra sessione. Chiudi e aggiorna il calendario.')); return;
      }
      if (original.start === pending.appointment.start && original.updated_at === pending.appointment.updated_at && movableAppointment(original)) {
        setUncertain(false); setError(message || tr('Nessuno spostamento confermato. Puoi riprovare.'));
      } else setError(tr('L’appuntamento è cambiato. Chiudi e aggiorna il calendario.'));
    } catch { setError(tr('Esito da verificare. Ripristina la connessione e premi Verifica esito prima di riprovare.')); }
  };
  const submit = async () => {
    if (flight.current || !draft || uncertain) return;
    flight.current = true; setBusy(true); setError('');
    try { done(await api.post(`/appointments/${draft.appointment.id}/reschedule`, draft.payload)); }
    catch (e) { await reconcile(draft, errMsg(e)); }
    finally { flight.current = false; if (alive.current) setBusy(false); }
  };
  const verify = async () => {
    if (flight.current || !draft) return;
    flight.current = true; setBusy(true);
    try { await reconcile(draft); } finally { flight.current = false; if (alive.current) setBusy(false); }
  };
  const close = () => { if (!flight.current) { setDraft(null); setUncertain(false); setError(''); current.current.onMoved(null); } };
  const bind = appointment => !usable || !movableAppointment(appointment) ? {} : {
    'data-smart-move': 'true', 'data-smart-hold': visual?.id === appointment.id ? (visual.armed ? 'ready' : 'waiting') : undefined,
    onPointerDown: e => { if (e.pointerType !== 'touch' && e.button === 0) startHold(e, appointment, 'pointer', e.pointerId, e.clientX, e.clientY); },
    onTouchStart: e => { if (e.touches.length !== 1) { cancel(); return; } const p = e.touches[0]; startHold(e, appointment, 'touch', p.identifier, p.clientX, p.clientY); },
    onContextMenu: e => e.preventDefault(), onDragStart: e => e.preventDefault(),
    onClickCapture: e => { if (Date.now() < suppress.current || gesture.current?.armed) { e.preventDefault(); e.stopPropagation(); } },
  };
  return { bind, enabled: available && enabled, blockClicks: checking || Boolean(draft) || Date.now() < suppress.current,
    toolbar: <div className="relative flex shrink-0 items-center gap-1.5" data-testid="smart-move-toolbar">
      <Button size="sm" className="h-9 rounded-full px-3" variant={enabled ? 'default' : 'outline'} aria-pressed={enabled} disabled={!available || !active || busy || checking || Boolean(draft)} onClick={toggle} data-testid="smart-move-toggle">{checking?tr('Verifica…'):tr('Sposta')}</Button>
      <details className="relative"><summary aria-label={tr('Come spostare un appuntamento')} className="grid h-8 w-8 cursor-pointer list-none place-items-center rounded-full border text-xs text-muted-foreground">?</summary><p className="absolute right-0 top-10 z-30 w-64 rounded-xl border bg-popover p-3 text-xs normal-case text-popover-foreground shadow-lg">{!available ? tr('Spostamento non disponibile.') : tr('Attiva Sposta, tieni premuto un appuntamento per un secondo, trascina e conferma. Nel mese e nell’agenda settimanale cambi giorno mantenendo l’orario; nella griglia oraria scegli anche l’orario. Per cambiare orario sul telefono apri Giorno. Disattiva Sposta quando hai finito.')}</p></details>
    </div>,
    overlay: available ? <>
      <style>{`[data-smart-move]{position:relative;-webkit-touch-callout:none;user-select:none;touch-action:auto}[data-smart-hold="waiting"]::after{content:"";position:absolute;bottom:0;left:0;height:4px;background:currentColor;animation:tpsSmartHold 1s linear forwards}[data-smart-hold="ready"]{outline:2px solid #e10600;opacity:.35}@keyframes tpsSmartHold{from{width:0}to{width:100%}}@media(prefers-reduced-motion:reduce){[data-smart-hold="waiting"]::after{animation:none;width:100%;opacity:.4}}`}</style>
      <span role="status" className="sr-only">{visual?.armed ? tr('Appuntamento sbloccato. Trascina verso il nuovo orario.') : ''}</span>
      {visual?.armed && createPortal(<div aria-hidden="true" className="pointer-events-none fixed inset-0 z-50" data-testid="smart-move-preview">
        {visual.target&&<div data-testid="smart-move-target" className="absolute rounded-md border-2 border-primary bg-zinc-800/25 shadow-sm" style={visual.target}/>}
        <div data-testid="smart-move-caption" className="absolute rounded-lg border border-primary bg-zinc-900/95 px-2.5 py-2 text-xs text-white shadow-lg" style={visual.caption}>
          {visual.title&&<div className="truncate text-[10px] text-white/65">{visual.title}</div>}
          <div className="font-semibold tabular-nums">{visual.start ? label(visual.start) : tr('Scegli un orario nella griglia')}</div>
        </div>
      </div>,document.body)}
      <Dialog open={Boolean(draft)} onOpenChange={value => { if (!value) close(); }}>
        <DialogContent><DialogHeader><DialogTitle>{tr('Conferma spostamento')}</DialogTitle></DialogHeader>
          {draft && <div className="space-y-3 text-sm">
            <p><strong>{tr('Da')}:</strong> {label(draft.appointment.start)}</p><p><strong>{tr('A')}:</strong> {label(draft.payload.start)}</p>
            <p>{tr('Durata, professionista e trattamento restano gli stessi. Il consenso già firmato viene conservato; data e materiali della seduta restano tracciati nel fascicolo.')}</p>
            {error && <p role="alert" className="text-destructive">{error}</p>}
          </div>}
          <DialogFooter><Button variant="outline" disabled={busy} onClick={close}>{tr('Annulla')}</Button>
            {uncertain ? <Button disabled={busy} onClick={verify}>{busy ? tr('Verifica…') : tr('Verifica esito')}</Button> : <Button disabled={busy} onClick={submit} data-testid="smart-move-confirm">{busy ? tr('Salvataggio…') : tr('Conferma spostamento')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </> : null };
}
